import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureStackedBarChartWithoutLegend } from '../../ChartConfiguration/ChartConfiguration';
import { getBreastCancerScreeningSummary } from '../HedisMetricService';
import BreastCancerScreeningSummary from '../../../Models/HedisMetrics/BreastCancerScreeningSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import { IconButton, Grid, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const BreastCancerScreeningSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [breastCancerScreeningSummary, setBreastCancerScreeningSummary] = useState<BreastCancerScreeningSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        breastCancerScreeningHealthPlans,
        setBreastCancerScreeningHealthPlans,
        breastCancerScreeningTeam,
        setBreastCancerScreeningTeam,
        breastCancerScreeningDateFilter,
        setBreastCancerScreeningDateFilter,
        breastCancerScreeningFromDate,
        setBreastCancerScreeningFromDate,
        breastCancerScreeningToDate,
        setBreastCancerScreeningToDate,
        breastCancerScreeningIncludePastOpenStatus,
        setBreastCancerScreeningIncludePastOpenStatus,
        breastCancerScreeningShowInActiveTeam,
        setBreastCancerScreeningShowInActiveTeam,
        setHedisType,
        setHealthPlans,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartWithoutLegend(
                    'Breast Cancer Screening',
                    'Screened vs Not Screened',
                    '',
                    'Count',
                    [{ name: '', data: [0] }],
                    [''],
                    null,
                    chartHeight
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (
            breastCancerScreeningDateFilter !== TimePeriodFilterEnum.Custom ||
            (Boolean(breastCancerScreeningFromDate) && Boolean(breastCancerScreeningToDate))
        ) {
            const changeFilter = async () => {
                let start = breastCancerScreeningFromDate !== null ? new Date(breastCancerScreeningFromDate).toDateString() : null;
                let end = breastCancerScreeningToDate !== null ? new Date(breastCancerScreeningToDate).toDateString() : null;

                const data = await getBreastCancerScreeningSummary(
                    breastCancerScreeningHealthPlans,
                    breastCancerScreeningTeam,
                    breastCancerScreeningDateFilter,
                    start,
                    end,
                    breastCancerScreeningIncludePastOpenStatus,
                    breastCancerScreeningShowInActiveTeam
                );
                setBreastCancerScreeningSummary(data);
                setIsPrepared(true);
            };
            if (
                (breastCancerScreeningFromDate !== null || breastCancerScreeningToDate !== null) &&
                breastCancerScreeningToDate < breastCancerScreeningFromDate
            ) {
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Breast Cancer Screening',
                        'Screened vs Not Screened',
                        '',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        breastCancerScreeningHealthPlans,
        breastCancerScreeningTeam,
        breastCancerScreeningDateFilter,
        breastCancerScreeningFromDate,
        breastCancerScreeningToDate,
        breastCancerScreeningIncludePastOpenStatus,
        breastCancerScreeningShowInActiveTeam,
    ]);

    useEffect(() => {
        if (breastCancerScreeningSummary && isPrepared) {
            let xAxisValues = [''];
            let yAxisValues = [];
            let yValues = [];
            let yVal;

            yValues = [];
            yVal = {
                y: breastCancerScreeningSummary.Screened,
                name: 'Screened',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Screened', data: yValues, pointPadding: 0, color: '#9ec7e8', showInLegend: true });

            yValues = [];
            yVal = {
                y: breastCancerScreeningSummary.NotScreened,
                name: 'Not Screened',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Screened', data: yValues, pointPadding: 0, color: '#6495ED', showInLegend: true });

            xAxisValues.push(breastCancerScreeningHealthPlans);

            if (breastCancerScreeningSummary.Screened === 0 && breastCancerScreeningSummary.NotScreened === 0) {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Breast Cancer Screening',
                        'Screened vs Not Screened',
                        '',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
            } else {
                setChartOptions(configureStackedBarChartWithoutLegend('Breast Cancer Screening', '', '', 'Count', yAxisValues, xAxisValues, navigateToMembers, chartHeight));
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breastCancerScreeningSummary, breastCancerScreeningHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlans(breastCancerScreeningHealthPlans);
        setTeam(breastCancerScreeningTeam);
        setFilter(breastCancerScreeningDateFilter);
        setFDate(breastCancerScreeningFromDate);
        setTDate(breastCancerScreeningToDate);
        setIncludePastOpenStatus(breastCancerScreeningIncludePastOpenStatus);
        setShowInActiveTeam(breastCancerScreeningShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.BreastCancerScreening);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/BreastCancerScreening`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            Breast Cancer Screening
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">
                                        For females 50-74 years of age, screened within the past two years
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Screened :
                                    </Typography>

                                    <Typography variant="caption">
                                        Any of the CPT codes - 77055, 77056, 77057, 77061, 77062, 77065, 77066, 77067, G0202, G0204, G0206
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Screened :
                                    </Typography>
                                    <Typography variant="caption">No CPT codes recorded</Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={breastCancerScreeningTeam}
                        setSelectedTeam={setBreastCancerScreeningTeam}
                        dateFilter={breastCancerScreeningDateFilter}
                        setDateFilter={setBreastCancerScreeningDateFilter}
                        fromDate={breastCancerScreeningFromDate}
                        setFromDate={setBreastCancerScreeningFromDate}
                        toDate={breastCancerScreeningToDate}
                        setToDate={setBreastCancerScreeningToDate}
                        healthPlan={breastCancerScreeningHealthPlans}
                        setHealthPlan={setBreastCancerScreeningHealthPlans}
                        includePastOpenStatusMembers={breastCancerScreeningIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setBreastCancerScreeningIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.BreastCancerScreening}
                        showInActiveTeams={breastCancerScreeningShowInActiveTeam}
                        setShowInActiveTeams={setBreastCancerScreeningShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default BreastCancerScreeningSummaries;
